"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const RSS_URL =
  "https://api.rss2json.com/v1/api.json?rss_url=https://castopod.jnwlabs.com/@quietlysecure/feed.xml";

export default function Home() {
  const [episodes, setEpisodes] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(true);

  async function fetchEpisodes() {
    const rssRes = await fetch(RSS_URL);
    const data = await rssRes.json();

    const parsed = data.items.map((item) => ({
      title: item.title,
      date: item.pubDate.split(" ")[0],
      audio: item.enclosure?.link,
      thumbnail: item.thumbnail || data.feed.image,
      link: item.link,
    }));

    setEpisodes(parsed.slice(0, 3));
  }

  async function fetchCerts() {
    try {
      const apiRes = await fetch("/api/certs");
      const data = await apiRes.json();
      setCerts(data);
    } catch (err) {
      console.error("Failed to fetch certs", err);
    } finally {
      setLoadingCerts(false);
    }
  }

  useEffect(() => {
    fetchEpisodes();
    fetchCerts();

    const interval = setInterval(fetchEpisodes, 600000);
    return () => clearInterval(interval);
  }, []);

  return (
    <main className="bg-slate-100 text-gray-800">
      {/* BANNER */}
      <div className="w-full h-48 md:h-64 overflow-hidden">
        <img src="/banner.jpg" className="w-full h-full object-cover" />
      </div>

      {/* NAVBAR */}
      <nav className="flex justify-between items-center px-8 py-4">
        <div className="font-bold text-blue-600">🔒 QuietlySecure</div>
        <div className="space-x-6 hidden md:block">
          <a href="#episodes">Episodes</a>
          <a href="#about">About</a>
          <a href="#certs">Certifications</a>
          <a href="#subscribe">Subscribe</a>
          <a href="#episodes" className="bg-blue-500 text-white px-4 py-2 rounded-full">
            Listen Now
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section className="flex flex-col md:flex-row justify-between items-center px-8 py-16">
        <div className="flex items-center gap-6">
          <img src="/logo.jpg" className="w-28 h-28 rounded-xl object-cover bg-gray-300" />
          <div>
            <p className="text-sm text-gray-500">CYBERSECURITY PODCAST</p>
            <h1 className="text-4xl font-bold">
              Quietly <span className="text-blue-500">Secure</span>
            </h1>
            <p className="text-gray-600 max-w-md mt-2">
              Bite-sized cybersecurity conversations — no hype, no jargon. Real talk about privacy, threats, and staying safe.
            </p>
          </div>
        </div>

        <div className="mt-6 md:mt-0">
          <a href="#episodes" className="bg-blue-500 text-white px-6 py-3 rounded-full inline-block">
            ▶ Latest Episodes
          </a>
        </div>
      </section>

      {/* EPISODES */}
      <section id="episodes" className="px-6 pb-20">
        <h2 className="text-3xl text-center font-bold mb-10">Recent Episodes</h2>

        <div className="max-w-3xl mx-auto space-y-6">
          {episodes.map((ep, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl shadow p-4 flex flex-col md:flex-row gap-4"
            >
              <img src={ep.thumbnail} className="w-full md:w-40 h-40 object-cover rounded-xl" />

              <div className="flex-1">
                <h3 className="text-lg font-semibold">{ep.title}</h3>
                <p className="text-sm text-gray-500">{ep.date}</p>

                {ep.audio && <audio controls className="w-full mt-3" src={ep.audio} />}

                <a href={ep.link} target="_blank" className="text-blue-500 text-sm mt-2 inline-block">
                  Open Episode →
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-slate-200 px-8 py-20 flex flex-col md:flex-row gap-10">
        <div className="max-w-lg">
          <h2 className="text-3xl font-bold">
            Security that <span className="text-blue-500">makes sense</span>
          </h2>
          <p className="text-gray-600 mt-4">
            Quietly Secure is a cybersecurity podcast created by Jonathan Wootton for anyone who wants to understand the digital world around them.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
          {[
            { title: "No Fluff", desc: "Straight to the point." },
            { title: "Privacy Focused", desc: "Tracking and identity explained." },
            { title: "Bite-Sized", desc: "Short and practical." },
            { title: "Accessible", desc: "Simple explanations." },
          ].map((item, i) => (
            <div key={i} className="bg-white p-4 rounded-xl shadow">
              <h4 className="font-semibold">{item.title}</h4>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section id="certs" className="px-6 py-20 bg-white">
        <h2 className="text-3xl text-center font-bold mb-10">Certifications</h2>

        {loadingCerts ? (
          <p className="text-center text-gray-500">Loading certifications...</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {certs.map((cert, i) => (
              <motion.a
                key={i}
                href={cert.url}
                target="_blank"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-slate-100 rounded-xl p-4 shadow hover:shadow-lg transition text-center"
              >
                <img src={cert.image} loading="lazy" className="w-20 h-20 mx-auto object-contain" />
                <h3 className="text-sm font-semibold mt-3">{cert.name}</h3>
                <p className="text-xs text-gray-500">{cert.issuer}</p>
              </motion.a>
            ))}
          </div>
        )}
      </section>

      {/* SUBSCRIBE */}
      <section id="subscribe" className="text-center py-20">
        <h2 className="text-3xl font-bold">Never miss an episode</h2>
      </section>

      {/* FOOTER */}
      <footer className="flex justify-between px-8 py-6 text-sm text-gray-500">
        <div>🔒 QuietlySecure</div>
        <div className="space-x-4">
          <a href="#episodes">Episodes</a>
          <a href="#certs">Certifications</a>
        </div>
        <div>© 2026 Quietly Secure</div>
      </footer>
    </main>
  );
}
